import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Timer, AlertTriangle, TrendingUp, CheckCircle2 } from 'lucide-react';

const CAT_LABELS = {
  umum: 'Maintenance Umum', me: 'M/E', it: 'IT',
  petty_cash: 'Petty Cash', refund_deposit: 'Refund Deposit',      // deprecated
  utility_payment: 'Utility', transfer_operasional: 'Transfer Ops',
  petty_cash_in: 'Petty Cash (IN)', petty_cash_out: 'Petty Cash (OUT)',
  deposit_in: 'Deposit (IN)', deposit_out: 'Deposit (OUT)',
};

const Metric = ({ label, value, sub, tone = 'default', icon: Icon }) => {
  const toneMap = {
    default: 'bg-background border-border',
    good: 'bg-green-50 border-green-300',
    warn: 'bg-amber-50 border-amber-300',
    bad: 'bg-red-50 border-red-300',
  };
  return (
    <div className={`rounded-lg border p-4 ${toneMap[tone]}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      </div>
      <p className="text-2xl font-medium tracking-tight">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
};

const SlaDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [respSla, setRespSla] = useState(24);
  const [resolSla, setResolSla] = useState(168);

  useEffect(() => {
    api.get('/locations').then((r) => setLocations(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (locationId !== 'all') params.set('location_id', locationId);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (respSla) params.set('response_sla', String(respSla));
    if (resolSla) params.set('resolution_sla', String(resolSla));
    api.get(`/dashboard/sla?${params}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [locationId, dateFrom, dateTo, respSla, resolSla]);

  const respBreachPct = useMemo(() => {
    if (!data || !data.total_tickets) return 0;
    return Math.round((data.response_breach_count / data.total_tickets) * 100);
  }, [data]);

  const resolBreachPct = useMemo(() => {
    if (!data || !data.total_tickets) return 0;
    return Math.round((data.resolution_breach_count / data.total_tickets) * 100);
  }, [data]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="sla-dashboard-page">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Monitoring</p>
          <h1 className="text-4xl font-medium tracking-tight flex items-center gap-3">
            <Timer className="w-8 h-8" /> SLA Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Waktu respon & penyelesaian tiket beserta pelanggaran SLA</p>
        </div>

        <Card className="border-border">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Cabang</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger className="h-9" data-testid="sla-filter-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Cabang</SelectItem>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Dari Tanggal</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="sla-filter-from" />
            </div>
            <div>
              <Label className="text-xs">Sampai</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="sla-filter-to" />
            </div>
            <div>
              <Label className="text-xs">SLA Respon (jam)</Label>
              <Input type="number" min="1" value={respSla} onChange={(e) => setRespSla(parseInt(e.target.value) || 24)} className="h-9" data-testid="sla-response-input" />
            </div>
            <div>
              <Label className="text-xs">SLA Penyelesaian (jam)</Label>
              <Input type="number" min="1" value={resolSla} onChange={(e) => setResolSla(parseInt(e.target.value) || 168)} className="h-9" data-testid="sla-resolution-input" />
            </div>
          </CardContent>
        </Card>

        {loading && (
          <div className="p-12 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          </div>
        )}

        {data && !loading && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Metric label="Total Tiket" value={data.total_tickets.toLocaleString('id-ID')} icon={TrendingUp} />
              <Metric label="Rata-rata Respon" value={`${data.avg_response_hours}h`}
                sub={`SLA: ${data.sla_thresholds.response_hours}h`}
                tone={data.avg_response_hours <= data.sla_thresholds.response_hours ? 'good' : 'warn'}
                icon={Timer} />
              <Metric label="Rata-rata Penyelesaian" value={`${data.avg_resolution_hours}h`}
                sub={`SLA: ${data.sla_thresholds.resolution_hours}h`}
                tone={data.avg_resolution_hours <= data.sla_thresholds.resolution_hours ? 'good' : 'warn'}
                icon={CheckCircle2} />
              <Metric label="Breach Respon"
                value={`${data.response_breach_count} (${respBreachPct}%)`}
                sub={`> ${data.sla_thresholds.response_hours}h`}
                tone={respBreachPct === 0 ? 'good' : respBreachPct > 20 ? 'bad' : 'warn'}
                icon={AlertTriangle} />
            </div>

            {/* Per Category */}
            <Card className="border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-base font-medium">SLA per Kategori</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.by_category.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground text-sm">Tidak ada data</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary/50 border-b border-border">
                        <tr>
                          <th className="text-left px-4 py-2 text-xs font-semibold">Kategori</th>
                          <th className="text-right px-4 py-2 text-xs font-semibold">Total</th>
                          <th className="text-right px-4 py-2 text-xs font-semibold">Avg Respon</th>
                          <th className="text-right px-4 py-2 text-xs font-semibold">Avg Resolusi</th>
                          <th className="text-right px-4 py-2 text-xs font-semibold">Breach Respon</th>
                          <th className="text-right px-4 py-2 text-xs font-semibold">Breach Resolusi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {data.by_category.map((c) => (
                          <tr key={c.category} data-testid={`sla-cat-${c.category}`}>
                            <td className="px-4 py-2">{c.category_label}</td>
                            <td className="px-4 py-2 text-right font-mono text-xs">{c.total}</td>
                            <td className="px-4 py-2 text-right font-mono text-xs">{c.avg_response_hours}h</td>
                            <td className="px-4 py-2 text-right font-mono text-xs">{c.avg_resolution_hours}h</td>
                            <td className="px-4 py-2 text-right">
                              {c.response_breach > 0 ? (
                                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">{c.response_breach}</Badge>
                              ) : <span className="text-muted-foreground text-xs">0</span>}
                            </td>
                            <td className="px-4 py-2 text-right">
                              {c.resolution_breach > 0 ? (
                                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">{c.resolution_breach}</Badge>
                              ) : <span className="text-muted-foreground text-xs">0</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Worst offenders */}
            {data.worst_offenders && data.worst_offenders.length > 0 && (
              <Card className="border-border">
                <CardHeader className="border-b border-border">
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Top 10 Tiket Terlama
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {data.worst_offenders.map((t) => (
                      <div key={t.ticket_number} className="p-3 flex items-center justify-between hover:bg-secondary/30" data-testid={`worst-${t.ticket_number}`}>
                        <div className="min-w-0 flex-1">
                          <Link to={`/tickets/${t.ticket_number}`} className="text-sm font-medium text-primary hover:underline">
                            {t.ticket_number}
                          </Link>
                          <p className="text-xs text-muted-foreground truncate">{t.title}</p>
                          <p className="text-[10px] text-muted-foreground">{CAT_LABELS[t.category] || t.category}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-semibold ${t.breach ? 'text-red-700' : ''}`}>
                            {Math.round(t.resolution_hours)}h
                          </p>
                          {t.still_open && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300 mt-1">Masih Terbuka</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default SlaDashboardPage;
